import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Popout } from '@connected/ui/components/popout';
import { Textarea } from '@connected/ui/components/textarea';
import { Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { CheckCircle2, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { queryClient } from '@/lib/query-client';

import type { ObservationLog } from '../utils/observation-log-types';

import { useObservationLogStatusMutation } from '../hooks/observation-log-status-mutation';

export type ObservationLogModalProps = {
    observationLog: ObservationLog;
    status: 'Approved' | 'Rejected';
};

type LogBookEntryForm = {
    message: string;
    files: string[];
};

export function ObservationLogModal({ observationLog, status }: ObservationLogModalProps) {
    const { t } = useTranslation('re-inspections');
    const [isOpen, setIsOpen] = useState(false);
    const { context: { electricalProjectVersionId } } = useElectricalProject();

    const DEFAULT_VALUES: LogBookEntryForm = {
        message: '',
        files: [],
    };

    const changeStatusMutation = useObservationLogStatusMutation({ electricalProjectVersionId });

    const form = useForm<LogBookEntryForm, ZodValidator>({
        defaultValues: DEFAULT_VALUES,
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            changeStatusMutation.mutate({
                body: {
                    ElectricalProjectObservationId: observationLog.ElectricalProjectObservationId!,
                    Message: value.message,
                    Status: status === 'Approved' ? 'SolutionApproved' : 'SolutionRejected',
                    Files: value.files.map(fileId => ({ ElectricalProjectFileId: fileId })),
                },
            });
            form.reset();
            setIsOpen(false);
        },
    });

    const electricalProjectFileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/observationlog',
        mutationOptions: {
            onMutate: async ({ ElectricalProjectFileId }) => {
                const previousFiles = form.state.values.files;

                const newFiles = [...previousFiles, ElectricalProjectFileId!];

                form.setFieldValue('files', newFiles);
            },
            onSettled: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.conclusion.observationLogsListOptions({ electricalProjectVersionId }));
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    });

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <div>
                {
                    status === 'Approved'
                        ? (
                                <Section.Header.Action onClick={() => setIsOpen(true)} icon={<CheckCircle2 className="text-success hover:text-success-hover" />} />
                            )
                        : (
                                <Section.Header.Action onClick={() => setIsOpen(true)} icon={<XCircle className="text-danger hover:text-danger-hover" />} />
                            )
                }
            </div>
            <Popout.Content aria-describedby="observation-content-description">
                <Popout.Header>
                    <Popout.Title>
                        {status === 'Approved'
                            ? 'Goedkeuren'
                            : 'Afkeuren'}
                    </Popout.Title>
                    <Popout.Description className="sr-only">
                        {status === 'Approved'
                            ? 'Goedkeuren en akkoord gaan met de huidige status en beoordeling.'
                            : 'Afkeuren en niet akkoord gaan met de huidige status of uitvoering'}
                    </Popout.Description>
                </Popout.Header>
                <Popout.Body className="gap-6 py-6">
                    <div className="flex flex-col gap-4">
                        <form.Field
                            name="message"
                            validators={{
                                onChange: validators.createElectricalProjectObservationLogCommand.message,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Opmerking" className="text-sm font-medium">
                                    <Textarea
                                        name={name}
                                        onChange={e => handleChange(e.target.value)}
                                        value={state.value || ''}
                                        placeholder="Noteer opmerking"
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </form.Field>
                        <form.Field
                            name="files"
                            children={() => (
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm font-medium">Foto's</p>
                                    <FileInput
                                        onChange={(fileList) => {
                                            const file = fileList[0];

                                            if (!file)
                                                return;

                                            electricalProjectFileUpload.upload({ file });
                                        }}
                                        accept={electricalProjectFileUpload.accept ?? undefined}
                                        disabled={electricalProjectFileUpload.isLoading}
                                    />

                                    <form.Subscribe
                                        selector={state => state.values.files}
                                    >
                                        {electricalProjectFiles => (
                                            <div className="flex flex-col gap-2">
                                                {electricalProjectFiles.map((electricalProjectFileId) => {
                                                    return (
                                                        <div key={electricalProjectFileId} className="relative flex items-center gap-2">
                                                            <ElectricalProjectImage
                                                                className="h-[112px] w-full rounded-lg"
                                                                query={{ ElectricalProjectFileId: electricalProjectFileId }}
                                                                expandable
                                                            />
                                                            <Button
                                                                className="absolute top-1 right-1 bg-background/50"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => {
                                                                    const previousFiles = form.state.values.files;

                                                                    const newFiles = previousFiles?.filter(fileId => fileId !== electricalProjectFileId);

                                                                    form.setFieldValue('files', newFiles);
                                                                }}
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </form.Subscribe>
                                    <p className="text-sm">U kunt meerdere foto's uploaden</p>
                                </div>
                            )}
                        />
                    </div>
                </Popout.Body>
                <Popout.Footer>
                    <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                    >
                        {t('actions.general.cancel_short', { ns: 'common' })}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={() => form.handleSubmit()}
                    >
                        {t('actions.general.save', { ns: 'common' })}
                    </Button>
                </Popout.Footer>
            </Popout.Content>
        </Popout>
    );
}
